/**
 * applyHtmlDirective — the html.* sub-switch of core (B).
 *
 * Lifted VERBATIM from the ui-action branch of useChat.sendMessage's SSE loop
 * (useChat.ts L1344-L1899, pre-decomposition). Handles `html.init`,
 * `html.chunk`, `html.status`, `html.complete`, and the section-level
 * `html.patch` / `html.insert` / `html.remove` directives. Every read/write of a
 * turn-local goes through `ctx.<field>`; every stable dep through `deps.<field>`.
 *
 * Behavioral notes preserved byte-for-byte:
 *   - the AbortError mid-create path re-throws so runChatStream's try/catch
 *     handles cleanup (was `throw err` propagating to sendMessage's catch).
 *   - the html.init `finally { htmlInitPending = false; htmlPendingBuffer = '' }`
 *     runs on every code path that entered the `else` branch, exactly as inline.
 *
 * Split out of the combined ui-action applier so neither sibling exceeds the
 * ~400 LOC ceiling. Pinned by useChat.html-stream-char.test.ts +
 * useChat.ui-directive-char.test.ts.
 */

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query/keys'
import { bumpPageTitleVersion } from './helpers/page-title-version'
import { findSectionRange, handleHtmlPatchDirective } from '../utils/hidden-directives'
import {
  setLiveHtml as setLiveHtmlState,
  clearLiveHtml as clearLiveHtmlState,
  getLiveHtml as getLiveHtmlState,
} from '@/lib/html-live-state'
import { stripStreamEnterAttr } from '../utils/stream-text'
import { toCanonicalPageId, rawPageId } from '@/lib/page-id'
import type { ChatUIAction } from '@/lib/api/endpoints/chat'
import type { TurnContext, ChatStreamDeps } from './useChatStream.types'

type UiAction = { type: string; [key: string]: unknown }

/**
 * Apply an html.* directive. Returns true when the action was an html.*
 * directive (so the caller knows it was handled), false when the action type is
 * not in this sub-switch.
 */
export async function applyHtmlDirective(
  ctx: TurnContext,
  action: UiAction,
  deps: ChatStreamDeps,
  signal: AbortSignal,
): Promise<boolean> {
  const {
    token,
    workspaceId,
    queryClient,
    activePageContext,
    activePageContextRef,
    lastSentContentRef,
    isTargetConversationActive,
  } = deps

  // Open the Pages container AND auto-select the building page inside it.
  // The API mirrors every html_viewer into a `pg_<moduleId>` page object at
  // create time and PagesContainerModule's merged list is object-primary
  // (the module row is hidden whenever its object mirror is live), so the
  // child must be addressed as an OBJECT (`o:pg_*`) — a 'module' childSource
  // would point at a row the dedup removed and the selection would never
  // resolve. Mirrors applyReportDirective's stash → focus → dispatch
  // ordering: the stash lands BEFORE the focus mounts the pane (the mount
  // consumes it), and the live dispatch fires after for the already-mounted
  // case.
  const openBuildingPageInPanel = (rawModuleId: string, pagesParentId: string | undefined) => {
    const canonicalChildId = pagesParentId
      ? toCanonicalPageId(rawModuleId) ?? rawModuleId
      : undefined
    if (pagesParentId) {
      // Refetch both row sources so the just-created entry can resolve —
      // when the Pages pane is already mounted there is no mount-time fetch
      // to pick the new page object up.
      queryClient.invalidateQueries({
        queryKey: queryKeys.objects.list({ type: 'page', moduleId: pagesParentId }),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) })
      try {
        window.sessionStorage.setItem(
          `xavior:pages-pending-child:${pagesParentId}`,
          JSON.stringify({ childModuleId: canonicalChildId, childSource: 'object', ts: Date.now() }),
        )
      } catch { /* sessionStorage unavailable */ }
    }
    deps.onUiActions?.([{
      type: 'module.focus',
      moduleType: 'pages',
      anchorMessageId: deps.assistantId,
      skipInvalidation: true,
    } as ChatUIAction])
    if (pagesParentId) {
      window.dispatchEvent(new CustomEvent('xavior:pages-open-child', {
        detail: { parentPagesId: pagesParentId, childModuleId: canonicalChildId, childSource: 'object' },
      }))
    }
  }

  if (action.type === 'html.init') {
    console.log('[useChat] html.init received', {
      moduleId: (action as any).moduleId,
      title: (action as any).title,
      hasActivePageContext: Boolean(activePageContextRef.current),
      activeModulesBefore: ctx.activeHtmlModules.size,
    })
    // Create html_viewer module and start streaming. Mark init pending
    // so any chunk events that arrive while createWorkspaceModule is
    // in flight get accumulated into htmlPendingBuffer instead of
    // dropped against an empty Map.
    let htmlTitle = typeof action.title === 'string' ? action.title : 'Page'

    // Auto-version: if a page with this exact title already exists, bump the version.
    // Pure NAME_RE math extracted to helpers/page-title-version.ts (Pass-2);
    // the cache read + try/catch wrapper stay here byte-identically.
    try {
      const cachedModules = queryClient.getQueryData<any[]>(queryKeys.modules.list(workspaceId))
      if (Array.isArray(cachedModules)) {
        const existingTitles = cachedModules
          .filter((m: any) => (m.type === 'html_viewer' || m.type === 'report') && m.label)
          .map((m: any) => m.label as string)
        htmlTitle = bumpPageTitleVersion(htmlTitle, existingTitles)
      }
    } catch (e) {
      console.warn('[useChat] Name dedup check failed:', e)
    }

    // Detect edit-of-existing-page: if the agent sends a moduleId
    // that matches the activePageContext we're already viewing,
    // reuse that module instead of creating a brand-new one.
    const incomingModuleId = typeof (action as { moduleId?: unknown }).moduleId === 'string'
      ? (action as { moduleId: string }).moduleId
      : undefined
    const isEditOfCurrentPage =
      activePageContext &&
      incomingModuleId &&
      ctx.activeHtmlModules.size === 0 &&
      incomingModuleId === activePageContext.rawId

    if (ctx.activeHtmlModules.size > 0) {
      // Defense against duplicate html.init (e.g. server transient
      // retry that re-runs the agent loop): reuse the existing
      // module instead of creating a second empty one that would
      // steal focus and wipe the partial content.
      console.warn('[useChat] Duplicate html.init received — resetting existing module buffer')
      for (const [id, mod] of ctx.activeHtmlModules) {
        mod.html = ''
        mod.headComplete = false
        mod.previewStarted = false
        mod.focused = false
        clearLiveHtmlState(id)
      }
      ctx.htmlInitPending = false
      ctx.htmlPendingBuffer = ''
    } else if (isEditOfCurrentPage) {
      // Editing the current page — reuse the existing module
      // rather than creating a new one. The html.chunk and
      // html.complete handlers will append and persist as usual
      // (html.complete already calls updateWorkspaceModule).
      console.log('[useChat] html.init for edit — reusing existing module', incomingModuleId)
      ctx.activeHtmlModules.set(incomingModuleId, {
        moduleId: incomingModuleId,
        html: '',
        headComplete: false,
        previewStarted: false,
        focused: false,
      })
      ctx.htmlModuleEverCreated = true
      // Update the title if the agent supplied a new one
      if (htmlTitle && htmlTitle !== activePageContext.title) {
        try {
          await api.workspaces.updateWorkspaceModule(token, workspaceId, incomingModuleId, {
            label: htmlTitle,
            config: { title: htmlTitle } as unknown as Record<string, unknown>,
          })
        } catch (titleErr) {
          console.warn('[useChat] Failed to update page title during edit:', titleErr)
        }
      }
      deps.onToolActivity?.()
    } else {
      ctx.htmlInitPending = true
      ctx.htmlPendingBuffer = ''
      try {
        // The server generates the moduleId in the html.init directive
        // and includes it as `moduleId` so the client and the server's
        // disconnect/graceful safety nets all target the same DB row
        // (the API endpoint upserts on existing id).
        const serverModuleId = typeof (action as { moduleId?: unknown }).moduleId === 'string'
          ? (action as { moduleId: string }).moduleId
          : undefined
        // Resolve the user's active Pages container module from the
        // React Query cache so the new html_viewer row is parented
        // correctly. This is the third leg of the parent_id
        // migration — new rows ship with parentId set, backfill
        // handles existing rows, and PagesContainerModule filters
        // by parentId with a type fallback. If no Pages module is
        // cached (e.g. modules list not yet fetched), we proceed
        // without parentId and let the server-side backfill catch
        // up so the page still appears in the list.
        let pagesParentId: string | undefined
        try {
          const cachedModules = queryClient.getQueryData<any[]>(queryKeys.modules.list(workspaceId))
          if (Array.isArray(cachedModules)) {
            const pagesModule = cachedModules.find(
              (m: any) => m?.type === 'pages' && !m?.archived && !m?.archivedAt,
            )
            if (pagesModule?.id) {
              pagesParentId = pagesModule.id
            }
          }
          if (!pagesParentId) {
            console.log('[useChat] No active Pages module found in cache; creating html_viewer without parentId (server backfill will reconcile)')
          }
        } catch (lookupErr) {
          console.warn('[useChat] Pages module lookup failed:', lookupErr)
        }
        const createdModule = await api.workspaces.createWorkspaceModule(token, workspaceId, {
          ...(serverModuleId ? { id: serverModuleId } : {}),
          type: 'html_viewer',
          label: htmlTitle,
          position: -1000,
          ...(pagesParentId ? { parentId: pagesParentId } : {}),
          config: { html: '', title: htmlTitle },
        })
        // If the user superseded this build mid-create, the SSE
        // fetch was aborted but the REST POST above still completed.
        // Delete the orphan immediately so it doesn't sit in the
        // Pages list as a blank entry. The next loop iteration will
        // see signal.aborted and break out.
        if (signal.aborted) {
          try {
            await api.workspaces.deleteWorkspaceModule(token, workspaceId, createdModule.id)
          } catch (delErr) {
            console.error('[useChat] Failed to delete orphan after abort:', delErr)
          }
          throw new DOMException('Aborted', 'AbortError')
        }
        const seedHtml = ctx.htmlPendingBuffer
        const seedHasBody = seedHtml.includes('</head>') && seedHtml.length > seedHtml.indexOf('</head>') + 20
        const newEntry = {
          moduleId: createdModule.id,
          html: seedHtml,
          headComplete: seedHtml.includes('</head>'),
          previewStarted: false,
          // Side-panel focus is deferred until the first real body
          // content arrives, so the user doesn't stare at a blank
          // container.
          focused: false,
          // Carried so the first-body chunk handler can auto-select this
          // page inside the Pages pane and html.complete can refresh the
          // exact page-object list feeding the rows.
          pagesParentId,
        }
        ctx.activeHtmlModules.set(createdModule.id, newEntry)
        ctx.htmlModuleEverCreated = true
        deps.onToolActivity?.()
        // If chunks arrived during init and head is already complete,
        // dispatch the first preview frame so the viewer renders.
        if (seedHtml && seedHtml.includes('</head>')) {
          newEntry.previewStarted = true
          setLiveHtmlState(createdModule.id, seedHtml)
          const dispatchId = toCanonicalPageId(createdModule.id, activePageContextRef.current) ?? createdModule.id
          window.dispatchEvent(new CustomEvent('xavior:html-config-update', {
            detail: { moduleId: dispatchId, html: seedHtml, mode: 'initial' },
          }))
        }
        // If the seed already contains real body content, this counts
        // as the first chunk — open the side panel now. Focus the
        // Pages container (so the back button + list are in scope)
        // and tell PagesContainerModule which page to auto-select
        // — same approach as the report path, except the child is
        // addressed as the OBJECT mirror (the html_viewer module row
        // is hidden by the object-primary dedup).
        if (seedHasBody) {
          newEntry.focused = true
          openBuildingPageInPanel(createdModule.id, pagesParentId)
        }
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') {
          ctx.htmlInitPending = false
          ctx.htmlPendingBuffer = ''
          throw err
        }
        console.error('[useChat] Failed to create html_viewer module:', err)
      } finally {
        ctx.htmlInitPending = false
        ctx.htmlPendingBuffer = ''
      }
    }
    return true
  } else if (action.type === 'html.chunk') {
    // Diagnostic: log every html.chunk receipt + active-modules count
    // so we can correlate with server emit when iframe stays blank.
    const chunkBytes = typeof action.html === 'string' ? action.html.length : 0
    const activeIds = Array.from(ctx.activeHtmlModules.keys())
    console.log('[useChat] html.chunk received', {
      kind: (action as any).kind,
      sectionId: (action as any).sectionId,
      bytes: chunkBytes,
      activeModules: activeIds.length,
      activeIds,
      htmlInitPending: ctx.htmlInitPending,
      pendingBufferLen: ctx.htmlPendingBuffer.length,
    })
    // Validate payload shape — silent drop on field drift was a real bug.
    if (typeof action.html !== 'string') {
      console.warn('[useChat] html.chunk missing string `html` field — dropped:', action)
    } else if (action.html) {
      const chunk = action.html
      // SectionFrame protocol: chunks may optionally carry `kind`
      // ('shell' | 'section') and `sectionId`. When present, the
      // client dispatches a per-section event that the iframe
      // applies in-place via postMessage — no srcDoc reassign,
      // no white flash. Legacy chunks (no kind) continue through
      // the cumulative-HTML preview path.
      const chunkKind = typeof (action as { kind?: unknown }).kind === 'string'
        ? (action as { kind: string }).kind
        : undefined
      const chunkSectionId = typeof (action as { sectionId?: unknown }).sectionId === 'string'
        ? (action as { sectionId: string }).sectionId
        : undefined
      if (ctx.htmlInitPending) {
        // Init still in flight — buffer until createWorkspaceModule resolves
        ctx.htmlPendingBuffer += chunk
      } else {
        // Append to all active modules (typically one)
        for (const [id, mod] of ctx.activeHtmlModules) {
          mod.html += chunk
          if (!mod.headComplete && mod.html.includes('</head>')) {
            mod.headComplete = true
          }
          if (!mod.headComplete) continue
          // Live-state mirror for this module. We previously called
          // stripStreamEnterAttr on every chunk which is O(n²) on
          // large builds; the attribute only matters at persistence
          // boundaries so the per-chunk copy can be raw. A final
          // strip runs at html.complete (below) and inside
          // setLiveHtmlState's persistence flush.
          setLiveHtmlState(id, mod.html)
          // Route by chunk kind. The four branches cover:
          //  1. kind:"section" + sectionId (after preview started) →
          //     dispatch a section-frame event; iframe mutates in
          //     place.
          //  2. kind:"shell" after previewStarted → hard reload
          //     (user restarted the build).
          //  3. First chunk that crossed </head> threshold — no
          //     preview yet — dispatch the initial preview.
          //  4. Fallthrough cumulative path: any chunk that reaches
          //     here with previewStarted=true but without a
          //     dispatching (legacy chunk, or kind:"section" without
          //     a sectionId) MUST still dispatch, or the iframe
          //     never sees the new bytes until html.complete. Strip
          //     the transient enter-animation marker here because
          //     this path dispatches cumulative HTML, not a section
          //     delta.
          // PageRenderer's section-update listener matches against
          // canonical pg_* (liveKey). Persistence stays raw (`id`).
          const dispatchId = toCanonicalPageId(id, activePageContextRef.current) ?? id
          if (mod.previewStarted && chunkKind === 'section' && chunkSectionId) {
            window.dispatchEvent(new CustomEvent('xavior:html-section-update', {
              detail: {
                moduleId: dispatchId,
                sectionId: chunkSectionId,
                html: chunk,
                fullHtml: mod.html,
              },
            }))
          } else if (mod.previewStarted && chunkKind === 'shell') {
            // New shell after a build already started — treat as
            // a fresh restart. PageRenderer covers the reload with
            // its loader so the user doesn't see a white flash.
            window.dispatchEvent(new CustomEvent('xavior:html-config-update', {
              detail: { moduleId: dispatchId, html: stripStreamEnterAttr(mod.html), mode: 'reload' },
            }))
          } else if (!mod.previewStarted) {
            mod.previewStarted = true
            window.dispatchEvent(new CustomEvent('xavior:html-config-update', {
              detail: { moduleId: dispatchId, html: stripStreamEnterAttr(mod.html), mode: 'initial' },
            }))
          } else {
            // Cumulative fallthrough: previewStarted is true but
            // either `kind` is missing (legacy chunk) or it was
            // `kind:"section"` without a sectionId (malformed). In
            // either case emit an incremental cumulative update so
            // the user actually sees the section before complete.
            window.dispatchEvent(new CustomEvent('xavior:html-config-update', {
              detail: { moduleId: dispatchId, html: stripStreamEnterAttr(mod.html), mode: 'incremental' },
            }))
          }
          // First real body content — open the side panel now
          // (was deferred from html.init so the user wouldn't see
          // a blank container) and auto-select the building page so
          // the user watches the live build without clicking. The
          // bare module.focus alone landed the user on the Pages
          // LIST while the build streamed unseen behind it.
          if (!mod.focused && mod.html.length > mod.html.indexOf('</head>') + 20) {
            mod.focused = true
            openBuildingPageInPanel(id, mod.pagesParentId)
          }
        }
      }
    }
    return true
  } else if (action.type === 'html.status') {
    // Mirror status to chat thinking indicator
    const msg = typeof action.message === 'string' ? action.message : ''
    if (msg) ctx.sawHtmlStatusMessage = true
    if (msg && isTargetConversationActive()) {
      deps.markXaviorStatusActivity()
      deps.setThinking(msg)
      deps.pushThinkingStatus(msg)
    }
    return true
  } else if (action.type === 'html.complete') {
    console.log('[useChat] html.complete received', {
      activeModules: ctx.activeHtmlModules.size,
      ids: Array.from(ctx.activeHtmlModules.keys()),
      totalHtmlBytes: Array.from(ctx.activeHtmlModules.values()).reduce((sum, m) => sum + m.html.length, 0),
    })
    // Finalize: flush all HTML, persist to API (with 1 retry), and
    // signal completion so the viewer hides the "Building page..."
    // indicator immediately (without waiting for a query refetch).
    let persistFailed = false
    for (const [id, mod] of ctx.activeHtmlModules) {
      // Strip the transient enter-animation marker before persist
      // and before the final dispatch so reloads render correctly.
      const finalHtml = stripStreamEnterAttr(mod.html)
      mod.html = finalHtml
      setLiveHtmlState(id, finalHtml, true)
      const dispatchId = toCanonicalPageId(id, activePageContextRef.current) ?? id
      // visual:false tells PageRenderer the sections are already in
      // place (streamed via xavior:html-section-update) — it must
      // NOT force a full iframe reload. The drain of
      // pendingSectionFramesRef still runs.
      window.dispatchEvent(new CustomEvent('xavior:html-config-update', {
        detail: { moduleId: dispatchId, html: finalHtml, final: true, visual: false },
      }))
      let savedOk = false
      for (let attempt = 0; attempt < 2 && !savedOk; attempt++) {
        try {
          await api.workspaces.updateWorkspaceModule(token, workspaceId, id, {
            config: { html: finalHtml } as unknown as Record<string, unknown>,
          })
          savedOk = true
        } catch (err) {
          console.error(`[useChat] html.complete persist attempt ${attempt + 1} failed:`, err)
          if (attempt === 0) {
            // Brief backoff before retry — usually a transient blip
            await new Promise(r => setTimeout(r, 500))
          }
        }
      }
      if (savedOk) {
        // Refetch the Pages row sources so the list preview reflects the
        // persisted html immediately. Without this the row keeps the empty
        // html from html.init until the user happens to click it (the row's
        // onClick was the only thing that invalidated these queries) — the
        // "broken thumbnail until clicked" symptom.
        queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) })
        if (mod.pagesParentId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.objects.list({ type: 'page', moduleId: mod.pagesParentId }),
          })
        } else {
          // Parent unknown (no Pages module cached at init) — refresh every
          // object list rather than miss the one feeding the rows.
          queryClient.invalidateQueries({ queryKey: queryKeys.objects.lists() })
        }
        // Queue a focus on the PAGES container (not the individual
        // html_viewer module) so the side panel header stays "PAGES"
        // rather than being replaced by the website title. The page
        // auto-opens within PagesContainerModule via xavior:html-config-update.
        deps.queuedUiActions.push({
          type: 'module.focus',
          moduleType: 'pages',
          anchorMessageId: deps.assistantId,
        } as ChatUIAction)
        deps.onToolActivity?.()
      } else {
        persistFailed = true
        // Both client-side attempts failed. Do NOT delete the module:
        // deleting destroys the just-built page, 404s its link, and races
        // x-agent's server-side safety net, which PATCHes the accumulated
        // _htmlBuffer to this same moduleId when the SSE stream closes — so
        // the content is recovered on refresh. Keep the live HTML visible and
        // surface a retryable error instead of vanishing the user's page.
        // (#pages-streaming-regression)
        console.error('[useChat] html.complete persist failed after retry; relying on server safety-net for', id)
      }
    }
    ctx.activeHtmlModules.clear()
    if (persistFailed && isTargetConversationActive()) {
      deps.setError(new Error('Page built but failed to save — please try again.'))
      deps.onError?.(new Error('Page built but failed to save — please try again.'))
    }
    return true
  } else if (action.type === 'html.patch' || action.type === 'html.insert' || action.type === 'html.remove') {
    // Section-level editing directives — patch/insert/remove a single
    // data-section-id element inside an already-built page.
    const sectionId = typeof action.sectionId === 'string' ? action.sectionId : ''
    const afterSectionId = typeof action.afterSectionId === 'string' ? action.afterSectionId : ''
    const objectId = typeof action.objectId === 'string' ? action.objectId : ''
    // html.insert uses afterSectionId instead of sectionId — accept either
    if (!sectionId && !afterSectionId) {
      console.warn(`[useChat] ${action.type} missing sectionId/afterSectionId — dropped:`, action)
    } else {
      // Resolve current full HTML from live state, active modules, or API fetch.
      // Check multiple keys: the directive's objectId, the active page's
      // module id, and any in-flight build modules.
      let currentHtml: string | undefined
      const htmlKeys = [objectId, activePageContext?.rawId].filter(Boolean)
      for (const key of htmlKeys) {
        if (!key) continue
        const live = getLiveHtmlState(key)
        if (live?.html) { currentHtml = live.html; break }
      }
      if (!currentHtml) {
        for (const [, mod] of ctx.activeHtmlModules) {
          if (mod.html) { currentHtml = mod.html; break }
        }
      }
      // Fallback: fetch from API when no local HTML source exists
      // (patch on a completed page with no active build). First try
      // the modules list (works for module-sourced pages), then the
      // page object API.
      if (!currentHtml && activePageContext?.rawId) {
        try {
          const allMods = await api.workspaces.listWorkspaceModules(token, workspaceId)
          const mod = allMods.find((m: any) => m.id === activePageContext!.rawId)
          if (mod?.config?.html) currentHtml = mod.config.html as string
        } catch { /* module fetch failed — try object API */ }
        if (!currentHtml) {
          try {
            const pageObjId = activePageContext.rawId.startsWith('pg_')
              ? activePageContext.rawId
              : `pg_${activePageContext.rawId}`
            const pageData = await api.objects.getObject(token, 'page', pageObjId)
            // `getObject` returns { object, etagKey }. The API mapper flattens
            // attributes to the top level, so html lives at object.html. Accept
            // object.attributes.html as a fallback in case the wire shape
            // changes (defense-in-depth).
            const pgObj = pageData?.object as
              | { html?: string; attributes?: { html?: string } }
              | undefined
            const fetchedHtml = pgObj?.html ?? pgObj?.attributes?.html
            if (typeof fetchedHtml === 'string' && fetchedHtml.length > 0) currentHtml = fetchedHtml
          } catch (err) {
            console.warn('[useChat] Failed to fetch page HTML for patch:', err)
          }
        }
      }
      if (!currentHtml) {
        console.warn(`[useChat] ${action.type} — no current HTML found for objectId=${objectId}`)
      } else {
        let patchedHtml = currentHtml

        // The section-delta payload sent on xavior:html-section-update
        // is the concrete new sectionId + html for the edit. For a
        // remove, the html is empty and the renderer is expected to
        // post `xavior-remove-section` instead of patch.
        let sectionDeltaHtml = ''
        let sectionDeltaId = ''
        if (action.type === 'html.patch') {
          // Prefer activePageContext.pageId over activeHtmlModules
          // because activeHtmlModules can hold a stale id from a
          // prior turn (the user may have switched pages).
          const rawTargetId = objectId
            || activePageContext?.pageId
            || (ctx.activeHtmlModules.keys().next().value as string)
            || activePageContext?.rawId
            || ''
          const canonicalTargetId = toCanonicalPageId(rawTargetId, activePageContextRef.current) ?? rawTargetId
          const patchResult = handleHtmlPatchDirective({
            moduleId: canonicalTargetId,
            sectionId,
            html: typeof action.html === 'string' ? action.html : '',
            currentHtml,
            userIntent: lastSentContentRef.current ?? undefined,
          })
          if (patchResult) {
            patchedHtml = patchResult.patchedHtml
            sectionDeltaHtml = patchResult.sectionDeltaHtml
            sectionDeltaId = patchResult.sectionDeltaId
          }
        } else if (action.type === 'html.insert') {
          const newHtml = typeof action.html === 'string' ? action.html : ''
          const afterId = typeof action.afterSectionId === 'string' ? action.afterSectionId : sectionId
          const range = findSectionRange(currentHtml, afterId)
          if (range) {
            patchedHtml = currentHtml.slice(0, range.end) + '\n' + newHtml + currentHtml.slice(range.end)
            sectionDeltaHtml = newHtml
            // Inserts carry their own sectionId on the inserted element;
            // fall back to the spec's sectionId field if provided.
            sectionDeltaId = sectionId || (() => {
              const m = newHtml.match(/data-section-id\s*=\s*["']([^"']+)["']/)
              return m ? m[1] : ''
            })()
          }
        } else if (action.type === 'html.remove') {
          const range = findSectionRange(currentHtml, sectionId)
          if (range) {
            patchedHtml = currentHtml.slice(0, range.start) + currentHtml.slice(range.end)
            // Empty html signals the section-update handler to
            // remove rather than patch.
            sectionDeltaHtml = ''
            sectionDeltaId = sectionId
          }
        }

        if (patchedHtml !== currentHtml) {
          // DISPATCH id (canonical pg_*, matches PageRenderer.liveKey) vs
          // PERSISTENCE id (the BARE html_viewer MODULE id — the only form
          // PATCH /workspaces/:ws/modules/:id resolves; its mirror cascade
          // then writes the page object). These are different id systems:
          // activePageContext.pageId is the COMPOSITE list-entry id
          // ('o:pg_*'), which 404s against /modules and silently loses the
          // edit on navigation — the section-edit persistence regression.
          // Every persistence candidate is therefore reduced via rawPageId
          // (strips 'o:'/'m:' then 'pg_'); the directive's explicit objectId
          // stays first so cross-page edits target the right page, then the
          // active page's resolved module id.
          const rawTargetId = objectId
            || activePageContext?.pageId
            || (ctx.activeHtmlModules.keys().next().value as string)
            || activePageContext?.rawId
          const canonicalDispatchId = rawTargetId
            ? (toCanonicalPageId(rawTargetId, activePageContextRef.current) ?? rawTargetId)
            : undefined
          const persistSource = objectId
            || activePageContext?.rawId
            || (ctx.activeHtmlModules.keys().next().value as string | undefined)
            || activePageContext?.pageId
          const persistTargetId = persistSource ? rawPageId(persistSource) : undefined
          if (persistTargetId && canonicalDispatchId) {
            // Keyed by the raw module id — consistent with the build flow's
            // live-state writes and PageRenderer's mount-time seed keys.
            setLiveHtmlState(persistTargetId, patchedHtml, true)
            // Route edits through the section-frame pipeline so
            // patch/insert/remove get the same fade-in transition
            // as streamed builds (and avoid the srcDoc flash the
            // full-reload path triggers). The cumulative dispatch
            // below is kept as belt-and-suspenders for persistence
            // and for renderers that don't implement the
            // section-update listener.
            if (sectionDeltaId) {
              window.dispatchEvent(new CustomEvent('xavior:html-section-update', {
                detail: {
                  moduleId: canonicalDispatchId,
                  sectionId: sectionDeltaId,
                  html: sectionDeltaHtml,
                  fullHtml: patchedHtml,
                },
              }))
            }
            window.dispatchEvent(new CustomEvent('xavior:html-config-update', {
              detail: { moduleId: canonicalDispatchId, html: patchedHtml, final: true },
            }))
            // Persist patched HTML — keys against the bare module id.
            // Skip if the action already had server-side persistence
            // (StructuralEditTool already PATCHed /sections/:sectionId).
            const serverPersisted = (action as { serverPersisted?: boolean }).serverPersisted === true
            if (!serverPersisted) {
              try {
                await api.workspaces.updateWorkspaceModule(token, workspaceId, persistTargetId, {
                  config: { html: patchedHtml } as unknown as Record<string, unknown>,
                })
              } catch (err) {
                console.error(`[useChat] ${action.type} persist failed:`, err)
                // A failed save here means the edit exists ONLY in the open
                // iframe — navigating away silently discards it. Surface the
                // failure instead of letting the user discover the loss later.
                if (isTargetConversationActive()) {
                  const persistErr = new Error('Edit applied but failed to save — please ask for the edit again.')
                  deps.setError(persistErr)
                  deps.onError?.(persistErr)
                }
              }
            }
          }
        }
      }
    }
    return true
  }

  return false
}
